'use client'

import { Badge } from '@/components/ui/badge'
import type { CandidateSkillsDomain, UnmappedSkill } from '@/hooks/use-evaluation-analysis'
import { cn } from '@/lib/utils'

/** Renders `candidate_skills_by_domain` from the analysis payload. */
export type CandidateSkillsByDomainProps = {
  domains: CandidateSkillsDomain[]
  unmapped?: UnmappedSkill[]
  /** When false, matching profile skills outside rubric domains are omitted. */
  showOther?: boolean
  /** Label for met job requirements not grouped in a rubric domain. */
  otherTitle?: string
  title?: string
  className?: string
}

export function CandidateSkillsByDomain({
  domains,
  unmapped,
  showOther = false,
  otherTitle = 'Other',
  title = 'Relevant skills by domain',
  className,
}: CandidateSkillsByDomainProps) {
  const hasOther = showOther && (unmapped?.length ?? 0) > 0
  if (domains.length === 0 && !hasOther) {
    return null
  }

  return (
    <section className={cn('space-y-3', className)}>
      <h3 className="text-sm font-semibold">{title}</h3>
      {domains.map((domain) => (
        <div key={domain.domain_key ?? domain.domain_label} className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">
            {domain.domain_label ?? domain.domain_key}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {domain.skills.map((skill) => (
              <Badge
                key={`${skill.canonical}-${skill.display}`}
                variant="secondary"
                className="font-normal"
                title={`Matched ${skill.match_type}${skill.confidence ? ` · ${Math.round(skill.confidence * 100)}%` : ''}`}
              >
                {skill.display !== skill.canonical
                  ? `${skill.display} → ${skill.canonical}`
                  : skill.display}
              </Badge>
            ))}
          </div>
        </div>
      ))}
      {hasOther ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{otherTitle}</p>
          <div className="flex flex-wrap gap-1.5">
            {(unmapped ?? []).map((skill) => (
              <Badge key={skill.display} variant="outline" className="font-normal">
                {skill.display}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  )
}
